import { unitLabel } from '@/lib/orders';

import type { ReplacementEligibleItem } from './types';

/** The Unit label helper lives in `@/lib/orders` (ADR-0024) — shared with warranty and, from ticket
 *  03, with `device-params`. Re-exported because this submodule's drawers import it from `./format`. */
export { unitLabel } from '@/lib/orders';

/** The eligible-items response is one entry per line item, but a request is raised against one
 *  Unit — so a `<SelectField>` option has to carry both. One composite string keeps the shared
 *  select untouched and makes the encoding a pure function in both directions (ADR-0022). */
const SEPARATOR = '#';

export function unitOptionValue(orderItemId: string, unitIndex: number): string {
  return `${orderItemId}${SEPARATOR}${unitIndex}`;
}

/** Precondition: `value` came from `unitOptionValue` — the select's options are the only source, so
 *  a separator is always present. A malformed string would yield `unitIndex: NaN`; no runtime guard
 *  is added because there is no path that can supply one. */
export function parseUnitOptionValue(value: string): { orderItemId: string; unitIndex: number } {
  const at = value.lastIndexOf(SEPARATOR);
  return {
    orderItemId: value.slice(0, at),
    unitIndex: Number(value.slice(at + 1)),
  };
}

/**
 * One option per eligible Unit, in response order. `eligibleUnits` decides which options exist and
 * carries the original numbers. A bundle needs no special case — it arrives as one entry.
 */
export function toUnitOptions(
  items: ReplacementEligibleItem[],
): Array<{ value: string; label: string }> {
  return items.flatMap((item) =>
    item.eligibleUnits.map((unitIndex) => ({
      value: unitOptionValue(item.orderItemId, unitIndex),
      label: unitLabel(item.productName, item.quantity, unitIndex),
    })),
  );
}
